import { Link } from 'react-router-dom';
import Logo from '../../components/feature/Logo';

export default function AGB() {
  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/agb`,
            "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/agb`,
            "name": "AGB – Allgemeine Geschäftsbedingungen | LearnSnap",
            "description": "Allgemeine Geschäftsbedingungen von LearnSnap. Nutzungsbedingungen für die KI-gestützte Lernplattform der Future Now UG.",
            "inLanguage": "de",
            "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/#website` },
            "about": {
              "@type": "Thing",
              "name": "Allgemeine Geschäftsbedingungen"
            }
          })
        }}
      />
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <Logo size="md" showText={true} textColor="text-slate-900" />
            </Link>
            <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-2"></i>Zurück zur Startseite
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Allgemeine Geschäftsbedingungen (AGB)</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-gray-600 mb-8">Stand: Januar 2025</p>

          {/* Important Disclaimer Box */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 mb-8 not-prose">
            <div className="flex items-start gap-3">
              <i className="ri-alert-line text-amber-600 text-2xl flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-amber-900 mb-2">Wichtiger Haftungsausschluss</h3>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Learnsnap ist eine KI-gestützte Lernplattform zur Unterstützung des selbstständigen Lernens. Wir übernehmen keine Garantie für Lernerfolge, Notenverbesserungen oder die Richtigkeit aller KI-generierten Inhalte. Die Nutzung erfolgt auf eigene Verantwortung.
                </p>
              </div>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Geltungsbereich</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der Lernplattform Learnsnap, 
              betrieben von Future Now UG, Max-Planck-Str. 4, 85609 Aschheim.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Mit der Registrierung und Nutzung von Learnsnap akzeptieren Sie diese AGB in vollem Umfang.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Leistungsbeschreibung</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Learnsnap bietet eine KI-gestützte Lernplattform mit folgenden Funktionen:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Personalisierte Übungsfragen basierend auf hochgeladenen Lernmaterialien</li>
              <li>KI-generierte Erklärungen und Lösungswege</li>
              <li>Fortschrittstracking und Lernstatistiken</li>
              <li>Intelligente Wiederholungssysteme (Spaced Repetition)</li>
              <li>Testvorbereitung und Lernpläne</li>
            </ul>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong>Wichtig:</strong> Learnsnap ist eine Lernunterstützung und kein Ersatz für schulischen Unterricht, 
                professionelle Nachhilfe oder pädagogische Betreuung. Die Plattform dient ausschließlich der Ergänzung 
                des selbstständigen Lernens.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Haftungsausschluss und Gewährleistung</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 KI-generierte Inhalte</h3>
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-900 leading-relaxed">
                <strong>Keine Garantie für Richtigkeit:</strong> Die von der KI generierten Fragen, Antworten und 
                Erklärungen können Fehler, Ungenauigkeiten oder veraltete Informationen enthalten. Wir übernehmen 
                keine Haftung für die inhaltliche Richtigkeit der KI-generierten Inhalte.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nutzer sind selbst dafür verantwortlich, die Richtigkeit der Lerninhalte zu überprüfen und bei 
              Unsicherheiten Lehrkräfte, Lehrbücher oder andere verlässliche Quellen zu konsultieren.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Keine Erfolgsgarantie</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir garantieren nicht:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Verbesserung von Schulnoten oder Prüfungsergebnissen</li>
              <li>Bestehen von Tests, Klausuren oder Prüfungen</li>
              <li>Erreichen bestimmter Lernziele oder Kompetenzniveaus</li>
              <li>Vollständigkeit oder Aktualität aller Lerninhalte</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Der Lernerfolg hängt von vielen individuellen Faktoren ab, einschließlich Motivation, Lernzeit, 
              Vorkenntnissen und persönlichem Engagement.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Technische Verfügbarkeit</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir bemühen uns um eine hohe Verfügbarkeit der Plattform, können jedoch keine 100%ige Erreichbarkeit 
              garantieren. Wartungsarbeiten, technische Störungen oder höhere Gewalt können zu temporären 
              Ausfällen führen. Eine Haftung für daraus entstehende Schäden wird ausgeschlossen.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.4 Hochgeladene Inhalte</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nutzer sind selbst verantwortlich für die Inhalte, die sie auf die Plattform hochladen. 
              Wir übernehmen keine Haftung für:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Urheberrechtsverletzungen durch hochgeladene Materialien</li>
              <li>Qualität oder Eignung der hochgeladenen Lernmaterialien</li>
              <li>Verlust oder Beschädigung hochgeladener Dateien</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Nutzerpflichten und Verantwortung</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 Eigenverantwortung</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Der Nutzer ist selbst verantwortlich für:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Die Überprüfung der Richtigkeit von KI-generierten Inhalten</li>
              <li>Die Einhaltung von Urheberrechten bei hochgeladenen Materialien</li>
              <li>Die sichere Aufbewahrung seiner Zugangsdaten</li>
              <li>Die Einhaltung dieser AGB und geltender Gesetze</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Verantwortung für hochgeladene Inhalte</h3>
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-4">
              <p className="text-sm text-blue-900 leading-relaxed mb-4">
                <strong>Nutzungsverantwortung:</strong> Der Nutzer ist verantwortlich, nur solche Inhalte (z. B. Screenshots oder Fotos) hochzuladen, deren Nutzung ihm erlaubt ist. Die Plattform verarbeitet diese Inhalte ausschließlich zum Zweck der individuellen Lernunterstützung.
              </p>
              <p className="text-sm text-blue-900 leading-relaxed mb-4">
                Die Analyse der hochgeladenen Inhalte erfolgt über sichere KI-Schnittstellen (APIs). Die dabei eingesetzten Dienste verarbeiten die Inhalte ausschließlich zur unmittelbaren Erledigung der Anfrage und <strong>verwenden sie nicht für Trainings- oder Weiterentwicklungszwecke ihrer Modelle.</strong>
              </p>
              <p className="text-sm text-blue-900 leading-relaxed">
                Die Plattform räumt sich ein einfaches, nicht übertragbares Nutzungsrecht ein, die hochgeladenen Inhalte technisch zu speichern, zu analysieren und dem Nutzer entsprechende Ergebnisse anzuzeigen. Eine darüber hinausgehende Nutzung der Inhalte, insbesondere zur KI-Modellentwicklung, findet nicht statt.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Verbotene Nutzung</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Folgende Handlungen sind untersagt:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Missbrauch der Plattform für illegale Zwecke</li>
              <li>Hochladen urheberrechtlich geschützter Materialien ohne Berechtigung</li>
              <li>Manipulation oder Reverse Engineering der KI-Systeme</li>
              <li>Weitergabe von Zugangsdaten an Dritte</li>
              <li>Automatisierte Massenabfragen oder Scraping</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Registrierung und Vertragsschluss</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Registrierung bei Learnsnap ist kostenlos. Mit der Registrierung kommt ein Nutzungsvertrag 
              auf Grundlage dieser AGB zustande. Minderjährige benötigen die Zustimmung ihrer Erziehungsberechtigten.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Preise und Zahlungsbedingungen</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Grundfunktionen von Learnsnap sind kostenlos nutzbar. Für Premium-Funktionen können 
              kostenpflichtige Abonnements angeboten werden. Alle Preise werden transparent auf der Website 
              angezeigt und verstehen sich inklusive der gesetzlichen Mehrwertsteuer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Kündigung und Vertragslaufzeit</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Kostenlose Accounts können jederzeit ohne Angabe von Gründen gekündigt werden. 
              Kostenpflichtige Abonnements haben die in der jeweiligen Produktbeschreibung angegebenen 
              Laufzeiten und Kündigungsfristen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Datenschutz</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Verarbeitung personenbezogener Daten erfolgt gemäß unserer{' '}
              <Link to="/datenschutz" className="text-teal-600 hover:underline font-semibold">
                Datenschutzerklärung
              </Link>
              . Hochgeladene Lernmaterialien werden zur KI-Analyse verarbeitet und können zur Verbesserung 
              unserer Dienste verwendet werden.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Haftungsbeschränkung</h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-700 leading-relaxed mb-3">
                <strong>Wichtiger Hinweis zur Haftung:</strong>
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                Die Haftung von Future Now UG ist auf Vorsatz und grobe Fahrlässigkeit beschränkt. 
                Für leichte Fahrlässigkeit haften wir nur bei Verletzung wesentlicher Vertragspflichten 
                (Kardinalpflichten) und nur in Höhe des vorhersehbaren, vertragstypischen Schadens.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Haftungsbeschränkung gilt nicht für Schäden aus der Verletzung des Lebens, des Körpers 
              oder der Gesundheit sowie bei Ansprüchen nach dem Produkthaftungsgesetz.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Änderungen der AGB</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir behalten uns das Recht vor, diese AGB jederzeit zu ändern. Nutzer werden über wesentliche 
              Änderungen per E-Mail informiert. Die Fortsetzung der Nutzung nach Änderungen gilt als 
              Zustimmung zu den neuen AGB.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Schlussbestimmungen</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Es gilt das Recht der Bundesrepublik Deutschland. Sollten einzelne Bestimmungen dieser AGB 
              unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Kontakt</h2>
            <div className="bg-slate-50 rounded-lg p-6">
              <p className="text-gray-700 mb-2"><strong>Future Now UG</strong></p>
              <p className="text-gray-700 mb-2">Max-Planck-Str. 4</p>
              <p className="text-gray-700 mb-2">85609 Aschheim</p>
              <p className="text-gray-700">
                E-Mail:{' '}
                <a href="mailto:info@future-now.online" className="text-teal-600 hover:underline">
                  info@future-now.online
                </a>
              </p>
            </div>
          </section>

          {/* Final Warning Box */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mt-8 not-prose">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-blue-600 text-2xl flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-blue-900 mb-2">Zusammenfassung</h3>
                <p className="text-sm text-blue-800 leading-relaxed">
                  Learnsnap ist eine Lernhilfe, die dich beim selbstständigen Lernen unterstützt. 
                  Wir können keine Garantie für Lernerfolge oder die Richtigkeit aller KI-Inhalte geben. 
                  Nutze die Plattform verantwortungsvoll und überprüfe wichtige Informationen immer mit 
                  deinen Lehrkräften oder Lehrbüchern. Bei Fragen stehen wir dir gerne zur Verfügung!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
